import React, { useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { XMarkIcon, PhotoIcon } from '@heroicons/react/24/outline';

const schema = yup.object({
  name: yup.string().required('Name is required').max(100, 'Name must be less than 100 characters'),
  description: yup.string().required('Description is required').max(500, 'Description must be less than 500 characters'),
  slug: yup.string().required('Slug is required').max(100, 'Slug must be less than 100 characters'),
});

const CategoryModal = ({ isOpen, onClose, onSubmit, category }) => {
  const isEdit = Boolean(category && !category.viewMode);
  const isViewMode = Boolean(category?.viewMode);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      slug: '',
    },
  });

  const watchedName = watch('name');

  useEffect(() => {
    if (category) {
      reset({
        name: category.name || '',
        description: category.description || '',
        slug: category.slug || '',
      });

      // Load image from multiple possible sources
      if (category.coverImage?.url || category.image_url || category.cover_image || category.image) {
        setImagePreview(category.coverImage?.url || category.image_url || category.cover_image || category.image);
      }
      setRemoveExistingImage(false);
    } else {
      reset({
        name: '',
        description: '',
        slug: '',
      });
      setImageFile(null);
      setImagePreview(null);
      setRemoveExistingImage(false);
      // Clear file input when modal resets
      const fileInput = document.getElementById('image-upload');
      if (fileInput) {
        fileInput.value = '';
      }
    }
  }, [category, reset]);

  // Auto-generate slug from name
  useEffect(() => {
    if (watchedName && !isEdit) {
      const slug = watchedName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setValue('slug', slug);
    }
  }, [watchedName, setValue, isEdit]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setRemoveExistingImage(false); // Reset remove flag when new image is selected
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setImageFile(null);
    setImagePreview(null);
    setRemoveExistingImage(true); // Flag that existing image should be removed
    // Clear the file input element
    const fileInput = document.getElementById('image-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleFormSubmit = async (data) => {
    try {
      // For create mode: Image is mandatory
      // For edit mode: Image is optional (can keep existing, remove, or replace)
      if (!isEdit && !imageFile && !imagePreview) {
        alert('Image is required for categories. Please upload an image.');
        return;
      }

      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('description', data.description || '');
      formData.append('slug', data.slug || '');

      // Handle image upload/removal
      if (imageFile) {
        // New image file selected (this takes priority over removeImage flag)
        formData.append('coverImage', imageFile); // Backend expects 'coverImage'
      } else if (removeExistingImage && isEdit) {
        // User clicked X to remove existing image (only in edit mode)
        formData.append('removeImage', 'true');
      }
      // If neither imageFile nor removeExistingImage, keep existing image (for edit mode)

      await onSubmit(formData);
      setImageFile(null);
      setImagePreview(null);
      setRemoveExistingImage(false);
    } catch (error) {
      console.error('Error submitting form:', error);
      throw error; // Re-throw to let parent handle it
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:ml-0 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                      {isViewMode ? 'View Category' : isEdit ? 'Edit Category' : 'Create New Category'}
                    </Dialog.Title>

                    <form onSubmit={handleSubmit(handleFormSubmit)} className="mt-6 space-y-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                          Name *
                        </label>
                        <input
                          {...register('name')}
                          type="text"
                          disabled={isViewMode}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                          placeholder="Enter category name"
                        />
                        {errors.name && (
                          <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="slug" className="block text-sm font-medium text-gray-700">
                          Slug *
                        </label>
                        <input
                          {...register('slug')}
                          type="text"
                          disabled={isViewMode}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                          placeholder="category-slug"
                        />
                        {errors.slug && (
                          <p className="mt-1 text-sm text-red-600">{errors.slug.message}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                          Auto-generated from name
                        </p>
                      </div>

                      <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                          Description *
                        </label>
                        <textarea
                          {...register('description')}
                          rows={3}
                          disabled={isViewMode}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                          placeholder="Enter category description"
                        />
                        {errors.description && (
                          <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Cover Image <span className="text-red-500">*</span>
                        </label>
                        {imagePreview ? (
                          <div className="relative">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="w-full h-40 object-cover rounded-lg border-2 border-gray-200"
                            />
                            {!isViewMode && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  removeImage(e);
                                }}
                                className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 shadow-lg transition-all"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ) : !isViewMode ? (
                          <div className="flex justify-center px-4 py-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-primary-400 transition-colors">
                            <div className="text-center">
                              <PhotoIcon className="mx-auto h-10 w-10 text-gray-400" />
                              <div className="mt-2 flex text-sm text-gray-600">
                                <label
                                  htmlFor="image-upload"
                                  className="relative cursor-pointer rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none"
                                >
                                  <span>Upload a file</span>
                                  <input
                                    id="image-upload"
                                    name="image-upload"
                                    type="file"
                                    accept="image/*"
                                    className="sr-only"
                                    onChange={handleImageChange}
                                  />
                                </label>
                                <p className="pl-1">or drag and drop</p>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 10MB</p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex justify-center px-4 py-6 border-2 border-gray-300 border-dashed rounded-lg bg-gray-50">
                            <div className="text-center">
                              <PhotoIcon className="mx-auto h-10 w-10 text-gray-400" />
                              <p className="text-sm text-gray-500 mt-2">No image uploaded</p>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                        {!isViewMode && (
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="inline-flex w-full justify-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 sm:col-start-2 disabled:opacity-50"
                          >
                            {isSubmitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                          </button>
                        )}
                        <button
                          type="button"
                          className={`mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 ${isViewMode ? 'sm:col-span-2' : 'sm:col-start-1'}`}
                          onClick={onClose}
                        >
                          {isViewMode ? 'Close' : 'Cancel'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default CategoryModal;
